import { useCallback, useMemo, useState } from 'react';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { clientApi } from '@tankef/api-client';
import { logger } from '@tankef/env';
import { useChannel, NotificationsChannel, wsClient, type Channel } from '@tankef/websocket';

import { useClientAuthStore } from '../../stores';

// Channels
const SystemAlertsChannel: Channel = { name: 'SystemAlertsChannel' };

/**
 * API response types matching backend
 */
type ApiNotification = {
  id: string;
  title?: string;
  notification_type?: string;
  body?: string;
  message?: string;
  priority?: string;
  read?: boolean;
  read_at?: string | null;
  created_at?: string;
  action_url?: string;
  category?: string;
  mandatory?: boolean;
};

type NotificationsQueryData = {
  data: ApiNotification[];
  meta?: { unread_count?: number };
};

/**
 * Notification interface for components
 */
export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  read: boolean;
  created_at: string;
  action_url?: string;
  action_label?: string;
  category?: string;
  mandatory?: boolean;
}

/**
 * System alert interface from ActionCable
 */
export interface SystemAlert {
  id?: string;
  severity: 'info' | 'warning' | 'critical';
  title: string;
  message: string;
  user_status_key?: string;
  alert_type?: string;
  timestamp?: string;
}

/**
 * Status of realtime connection
 */
export type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'polling';

/**
 * Map API notification to Notification type
 */
// Only compliance/security/legal notifications can block the UI.
// Transactional notifications (transfers, deposits) must never be mandatory
// even if the backend incorrectly sends mandatory: true.
const MANDATORY_ALLOWED_CATEGORIES = new Set(['compliance', 'security', 'legal', 'regulatory']);

function mapApiNotification(n: ApiNotification): Notification {
  const categoryAllowsMandatory = n.category ? MANDATORY_ALLOWED_CATEGORIES.has(n.category) : false;

  return {
    id: n.id,
    title: n.title || n.notification_type || 'Notificación',
    message: n.body || n.message || '',
    type: (n.priority === 'high' ? 'warning' : 'info') as 'warning' | 'info',
    read: n.read ?? Boolean(n.read_at),
    created_at: n.created_at || new Date().toISOString(),
    action_url: n.action_url,
    action_label: 'Ver',
    category: n.category,
    mandatory: (n.mandatory ?? false) && categoryAllowsMandatory,
  };
}

/**
 * useRealtimeNotifications
 *
 * Manages both websocket realtime notifications and REST API polling.
 * Falls back to polling if websocket connection fails.
 *
 * Uses React Query as single source of truth for notifications state.
 * WebSocket handlers update via queryClient.setQueryData (not setState).
 *
 * Features:
 * - Auto-connection to NotificationsChannel after login
 * - SystemAlerts via SystemAlertsChannel
 * - REST fallback with exponential backoff
 * - Unread count tracking
 * - Mark as read functionality
 * - Local state updates without full page reload
 *
 * @example
 * ```tsx
 * const { notifications, unreadCount, systemAlert } = useRealtimeNotifications();
 *
 * return (
 *   <>
 *     <NotificationBell count={unreadCount} />
 *     {systemAlert && <SystemAlertBanner alert={systemAlert} />}
 *   </>
 * );
 * ```
 */
export function useRealtimeNotifications() {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useClientAuthStore();

  // Local state: only systemAlert (not in API)
  const [systemAlert, setSystemAlert] = useState<SystemAlert | null>(null);

  /**
   * Query for notifications from REST API
   */
  const notificationsQuery = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      const response = await clientApi.notifications?.();
      if (!response) {
        throw new Error('API notifications not available');
      }
      return response as NotificationsQueryData;
    },
    enabled: isAuthenticated,
    refetchInterval: wsClient.isConnected() ? false : 30 * 1000, // Poll every 30s if not connected
    staleTime: 10 * 1000, // 10 seconds
  });

  /**
   * Mutation to mark notification as read
   */
  const markAsReadMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      return clientApi.markNotificationAsRead?.(notificationId);
    },
    onMutate: async (notificationId: string) => {
      // Optimistic update via setQueryData
      queryClient.setQueryData<NotificationsQueryData>(['notifications'], old => {
        if (!old) return old;
        const updated = old.data.map(n => (n.id === notificationId ? { ...n, read: true } : n));
        const newUnread = Math.max(0, (old.meta?.unread_count || 0) - 1);
        return {
          data: updated,
          meta: { ...old.meta, unread_count: newUnread },
        };
      });
    },
    onError: () => {
      // Refetch on error to restore state
      notificationsQuery.refetch();
    },
  });

  /**
   * Mutation to mark all notifications as read
   */
  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      // API call if available
      return clientApi.markAllNotificationsAsRead?.();
    },
    onMutate: async () => {
      queryClient.setQueryData<NotificationsQueryData>(['notifications'], old => {
        if (!old) return old;
        const updated = old.data.map(n => ({ ...n, read: true }));
        return {
          data: updated,
          meta: { ...old.meta, unread_count: 0 },
        };
      });
    },
    onError: () => {
      notificationsQuery.refetch();
    },
  });

  /**
   * Handle new notification message
   */
  const handleNotificationMessage = useCallback(
    (data: unknown) => {
      const envelope = data as Record<string, unknown>;
      const type = envelope.type as string | undefined;
      const payload = (envelope.payload as Record<string, unknown> | undefined) ?? envelope;

      if (type === 'new_notification') {
        queryClient.setQueryData<NotificationsQueryData>(['notifications'], old => {
          if (!old) return old;
          const newNotif = payload.notification as ApiNotification;
          return {
            data: [newNotif, ...old.data],
            meta: {
              ...old.meta,
              unread_count:
                (payload.unread_count as number | undefined) ?? (old.meta?.unread_count || 0) + 1,
            },
          };
        });
      } else if (type === 'unread_count_updated') {
        queryClient.setQueryData<NotificationsQueryData>(['notifications'], old => {
          if (!old) return old;
          return {
            data: old.data,
            meta: {
              ...old.meta,
              unread_count:
                (payload.unread_count as number | undefined) ?? (payload.count as number),
            },
          };
        });
      } else if (type === 'notification_read') {
        queryClient.setQueryData<NotificationsQueryData>(['notifications'], old => {
          if (!old) return old;
          const notifId = payload.notification_id as string;
          const updated = old.data.map(n => (n.id === notifId ? { ...n, read: true } : n));
          const newUnread =
            (payload.unread_count as number | undefined) ??
            Math.max(0, (old.meta?.unread_count || 0) - 1);
          return {
            data: updated,
            meta: { ...old.meta, unread_count: newUnread },
          };
        });
      } else if (type === 'notifications_cleared') {
        queryClient.setQueryData<NotificationsQueryData>(['notifications'], old => {
          if (!old) return old;
          return {
            data: [],
            meta: { ...old.meta, unread_count: 0 },
          };
        });
      } else if (type === 'recent_notifications' || type === 'batch_notifications') {
        const notifications = (payload.notifications as ApiNotification[] | undefined) ?? [];

        queryClient.setQueryData<NotificationsQueryData>(['notifications'], old => ({
          data: notifications,
          meta: {
            ...old?.meta,
            unread_count: (payload.unread_count as number | undefined) ?? old?.meta?.unread_count,
          },
        }));
      }

      logger.debug('[useRealtimeNotifications] Notification message:', {
        type,
      });
    },
    [queryClient]
  );

  /**
   * Handle system alert message
   */
  const handleSystemAlertMessage = useCallback((data: unknown) => {
    const envelope = data as Record<string, unknown>;
    const type = envelope.type as string | undefined;
    const payload = (envelope.payload as Record<string, unknown> | undefined) ?? envelope;

    if (type === 'system_alert') {
      setSystemAlert({
        severity: (payload.severity as SystemAlert['severity']) ?? 'info',
        title: (payload.title as string) ?? 'System Alert',
        message: (payload.message as string) ?? '',
        user_status_key: payload.user_status_key as string | undefined,
        alert_type: payload.alert_type as string | undefined,
        timestamp: (envelope.timestamp as string | undefined) ?? new Date().toISOString(),
      });

      logger.info('[useRealtimeNotifications] System alert received');
    }
  }, []);

  /**
   * Subscribe to notification and system alert channels
   */
  useChannel(NotificationsChannel, handleNotificationMessage, isAuthenticated);
  useChannel(SystemAlertsChannel, handleSystemAlertMessage, isAuthenticated);

  /**
   * Derived state from query
   */
  const notifications: Notification[] = useMemo(() => {
    return (notificationsQuery.data?.data ?? []).map(mapApiNotification);
  }, [notificationsQuery.data?.data]);

  const unreadCount = notificationsQuery.data?.meta?.unread_count ?? 0;

  const connectionStatus: ConnectionStatus = wsClient.isConnected()
    ? 'connected'
    : isAuthenticated
      ? 'polling'
      : 'disconnected';

  /**
   * Mark notification as read
   */
  const markAsRead = useCallback(
    (notificationId: string) => {
      markAsReadMutation.mutate(notificationId);
    },
    [markAsReadMutation]
  );

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = useCallback(() => {
    markAllAsReadMutation.mutate();
  }, [markAllAsReadMutation]);

  /**
   * Dismiss system alert
   */
  const dismissSystemAlert = useCallback(() => {
    setSystemAlert(null);
  }, []);

  /**
   * Clear all notifications
   */
  const clearAllNotifications = useCallback(() => {
    queryClient.setQueryData<NotificationsQueryData>(['notifications'], old =>
      old
        ? {
            data: [],
            meta: { ...old.meta, unread_count: 0 },
          }
        : old
    );
  }, [queryClient]);

  return {
    // State
    notifications,
    systemAlert,
    unreadCount,
    connectionStatus,
    isLoading: notificationsQuery.isLoading,
    isFetching: notificationsQuery.isFetching,

    // Actions
    markAsRead,
    markAllAsRead,
    dismissSystemAlert,
    clearAllNotifications,

    // Utils
    hasSystemAlert: !!systemAlert,
    isOnline: connectionStatus !== 'disconnected',
  };
}
